// V94.42 — Generation IA batch de recettes detaillees pour un plan
// Output strict JSON pour matching deterministe avec les meals existants.
// Modele : Claude Haiku 4.5 (rapide, economique, niveau OK pour recettes).
//
// Usage cote SaaS :
//   const recipes = await generateRecipesForMeals(meals, { form, allergies, locale });
//   // recipes = { [mealKey]: { ingredients, ingredients_structured, preparation, ... } }
//
// Le generateur respecte STRICTEMENT les ingredients du titre du repas (pas
// d'inventions). Si la cliente a des allergies/regimes, on les transmet et l'IA
// les respecte (pas de gluten, vegetarien, etc.).

import { ANISSA_IDENTITY_CORE } from "./prompts/nutrition/identity.fr";
// V97.0 : safeParseJson centralise dans anthropic.js, callClaude unifie
import { callClaude, safeParseJson, ClaudeApiError } from "./anthropic";
// V97.58 (audit P4.7) : le generateur de recettes recoit le MEME bloc securite
// que les plans — contraintes maternelles SCELLEES (jeune, restriction
// calorique, vitamine A, iode), flags profil a risque, allergenes,
// restrictions religieuses, traitements, Ramadan. Avant : seul le user message
// portait allergies/pathologies.
// PERIMETRE HONNETE (revue clinique 2026-07-02) : le bloc maternel valide
// Anissa (12 juin 2026) ne contient PAS d'evictions alimentaires listeria/
// toxoplasmose (poisson fume, lait cru, viande crue...). Les ajouter est une
// DECISION CLINIQUE qui appartient a Anissa — question ouverte, a trancher
// avant septembre. Ne pas l'ajouter en douce ici.
import { buildSafetyBlockFr } from "./prompts/nutrition/_clinicalContext.fr";
import { buildSafetyBlockEn } from "./prompts/nutrition/en";
import { redactDirectIdentifiers } from "./externalPlanWorkflow";
import { NO_SELF_MENTION_INSTRUCTION } from "./aiMentionGuard";
import { hasRiskProfileAssessment } from "./riskProfileFlags";
import { buildRecipeCulinaryReferencePrompt } from "./recipeCulinaryReferences";
import {
  formatStructuredIngredient,
  normalizeStructuredIngredientsWithDiagnostics,
  readStructuredRecipeIngredients,
  STRUCTURED_INGREDIENT_CATEGORIES,
  STRUCTURED_INGREDIENT_UNITS,
} from "./structuredRecipe";

const SYSTEM_PROMPT_BODY = `CONTEXTE : Generation de recettes detaillees pour le plan nutrition d'une
cliente. Anissa peaufine ce plan avant publication dans son app cliente.

TON : pratique, rassurant, signature Anissa. Phrases courtes. Tutoiement
(Anissa tutoie ses clientes dans le plan) : "tu", "ton", "tes".

----- TES OBLIGATIONS -----

Pour CHAQUE repas fourni dans la liste, generer une recette EXPLOITANT
EXCLUSIVEMENT les ingredients implicites du titre + les regles ci-dessous.

1. INGREDIENTS STRUCTURES
   - 5 a 10 objets dans "ingredients_structured"
   - Quantites realistes pour 1 personne (sauf si "servings" sup. demande)
   - Chaque objet contient EXACTEMENT : id, foodKey, label, quantity, unit, preparation, category
   - foodKey : slug stable en minuscules. Utilise le MEME foodKey pour un meme
     aliment dans toutes les recettes du lot, par exemple "poulet" ou "quinoa"
   - quantity : nombre strictement positif. Utilise null uniquement avec
     unit="to_taste"
   - unit : une valeur parmi ${STRUCTURED_INGREDIENT_UNITS.join(", ")}
   - preparation : texte court ou chaine vide
   - category : une valeur parmi ${STRUCTURED_INGREDIENT_CATEGORIES.join(", ")}
   - PAS d'ingredients exotiques ou rares non mentionnes dans le titre

2. PREPARATION
   - Liste numerotee (array de strings, 4 a 7 etapes)
   - Etapes courtes et concretes (1-2 phrases max chacune)
   - Verbes d'action en debut (Faire cuire, Emincer, Dresser, Arroser...)
   - Tutoiement : "Fais cuire", PAS "Faites cuire"

3. TEMPS
   - prep_time_min : entier (preparation hors cuisson)
   - cook_time_min : entier (cuisson, 0 si pas de cuisson)
   - servings : entier (1 par defaut sauf indication contraire)

4. CONSEIL D'ANISSA (tip)
   - 1 phrase courte (max 25 mots)
   - Conseil uniquement culinaire ou pratique : texture, preparation,
     organisation. Aucune promesse de sante, conservation ou interpretation.
   - Exemple OK : "Ajoute les herbes juste avant de servir."

----- ANTI-AI WRITING (V94.54) -----

Le "tip" (conseil d'Anissa) doit sonner humain, pas ChatGPT.

INTERDIT dans les tips :
- "Veritable allie de", "joue un role cle", "cle de voute"
- "richesse en", "vibrant", "groundbreaking"
- Listes avec "et" final mecanique : "X, Y et Z"
- Tirets cadratins (—), preferer virgules
- "Ce n'est pas seulement X, mais aussi Y" (parallelisme negatif)
- Conclusions generiques : "Pour un bien-etre optimal", "Une excellente
  facon de prendre soin de vous"

ECRIRE plutot des tips concrets et specifiques :
- "Prepare la sauce pendant la cuisson des legumes."
- "Ajoute les herbes juste avant de servir."
- "Laisse refroidir cinq minutes avant de dresser."

Pas de "trois ingredients pour" / "trois bienfaits". Variez le nombre.
Voix d'Anissa : pratique, posee, sans emphase commerciale.

----- ALLERGIES, REGIMES & DIRECTIVES D'ANISSA -----

Si le profil cliente mentionne des allergies (gluten, lactose, fruits a coque,
etc.) ou un regime (vegetarien, sans porc, etc.) : ADAPTER les ingredients et
substituer ce qui est incompatible. Ne JAMAIS suggerer un ingredient interdit.

Si des DIRECTIVES D'ANISSA sont fournies dans le message, RESPECTE-LES
ABSOLUMENT : tout aliment qu'Anissa signale comme refuse / non aime (ex.
"n'aime pas les oeufs", "ne mange pas de porc") ne doit apparaitre dans AUCUN
ingredient ni AUCUNE etape de la recette. Un refus / une aversion signale par
Anissa est aussi contraignant qu'une allergie.

----- OUTPUT -----

UNIQUEMENT du JSON valide, sans texte avant/apres, sans markdown, sans backticks.

{
  "recipes": {
    "[mealKey]": {
      "ingredients_structured": [
        {
          "id": "ingredient-1",
          "foodKey": "saumon",
          "label": "Saumon",
          "quantity": 120,
          "unit": "g",
          "preparation": "en filet",
          "category": "protein"
        }
      ],
      "preparation": ["string", ...],
      "prep_time_min": int,
      "cook_time_min": int,
      "servings": int,
      "tip": "string"
    },
    ...
  }
}

La cle de chaque recette DOIT etre EXACTEMENT le "key" du repas tel que fourni
dans le user message. Aucune autre cle ne doit etre presente.

REGLE ABSOLUE : si le titre d'un repas est generique, ambigu ou ne permet pas
d'identifier les aliments a cuisiner, n'invente rien et ne renvoie aucune
recette pour cette cle. Le SaaS bloquera le brouillon pour validation.
`;

/**
 * V97.58 — Assemble le system prompt recettes AVEC le bloc securite clinique,
 * injecte juste apres l'identite (meme position de priorite que sur les chemins
 * de plan FR/EN). Exportee pure pour etre testable sans appel IA.
 *
 * @param {object} form - fiche cliente (source du bloc securite)
 * @param {string} [locale] - 'fr' | 'en' (bloc dans la langue du prompt)
 * @returns {string}
 */
export function buildRecipeSystemPrompt(form = {}, locale = "fr") {
  const safetyForm = sanitizeRecipeForm(form);
  const safety = locale === "en" ? buildSafetyBlockEn(safetyForm) : buildSafetyBlockFr(safetyForm);
  return [
    ANISSA_IDENTITY_CORE,
    safety,
    buildRecipeCulinaryReferencePrompt(),
    SYSTEM_PROMPT_BODY,
    NO_SELF_MENTION_INSTRUCTION,
  ].filter(Boolean).join("\n\n");
}

const EXPLICIT_ABSENCE_RE = /^(?:(?:aucun(?:e|es|s)?)(?:\s+(?:allergies?|intol[ée]rances?|restrictions?(?:\s+alimentaires?)?|aliments?\s+(?:[àa]\s+)?[ée]viter))?|n[ée]ant|ras|r\.a\.s\.?|non|none|nothing|no\s+(?:known\s+)?(?:allerg(?:y|ies)|intolerances?|restriction(?:s)?)|pas\s+d['’ ]?(?:allergies?|intol[ée]rances?|restrictions?))(?:\s+(?:connues?|d[ée]clar[ée]es?))?\.?$/i;

function normalizeProfileValue(value) {
  if (Array.isArray(value)) {
    return value
      .map((item) => normalizeProfileValue(item))
      .filter(Boolean)
      .join(", ");
  }
  if (value === null || value === undefined || value === false) return "";
  const normalized = String(value).replace(/\s+/g, " ").trim();
  return EXPLICIT_ABSENCE_RE.test(normalized) ? "" : normalized;
}

function redactProfileValue(value, identity) {
  const normalized = normalizeProfileValue(value);
  return normalized ? redactDirectIdentifiers(normalized, identity) : "";
}

export function sanitizeRecipeForm(form = {}) {
  if (!form || typeof form !== "object") return {};
  return Object.fromEntries(Object.entries(form).map(([key, value]) => {
    if (typeof value === "string" || Array.isArray(value)) {
      const redacted = redactProfileValue(value, form);
      return [key, Array.isArray(value) ? (redacted ? redacted.split(/\s*,\s*/) : []) : redacted];
    }
    return [key, value];
  }));
}

export function sanitizeRecipeDirectives(directives, form = {}) {
  return redactProfileValue(directives, form);
}

function collectProfileValues(form, keys) {
  const seen = new Set();
  return keys
    .map((key) => redactProfileValue(form?.[key], form))
    .filter((value) => {
      const canonical = value.toLocaleLowerCase("fr");
      if (!value || seen.has(canonical)) return false;
      seen.add(canonical);
      return true;
    })
    .join(" | ");
}

/**
 * Contexte alimentaire minimal transmis aux generateurs recettes/alternatives.
 * Liste blanche volontaire : aucune identite, coordonnee ou note libre globale.
 * Les reponses explicites « aucune / pas d'allergie » sont omises pour ne
 * jamais etre interpretees comme des aliments a exclure.
 */
export function buildRecipeProfileContextLines(form = {}) {
  const groups = [
    ["Allergies declarees", ["allergies"]],
    ["Intolerances et aliments a eviter", [
      "alimentsEvites", "intolerances", "intolerancesAlimentaires",
    ]],
    ["Restrictions alimentaires", [
      "restrictionsAlimentaires", "restrictionsAutre", "regimesSuivis",
    ]],
    ["Aliments refuses ou non aimes", [
      "alimentsRefuses", "alimentsRefusés", "alimentsNonAimes", "alimentsNonAimés",
    ]],
    ["Preferences alimentaires", [
      "preferencesAlimentaires", "alimentsAimes", "alimentsAimés",
    ]],
    ["Objectifs", [
      "objectifPrincipalNutrition", "objectif_primaire", "objectif_secondaire_1",
      "objectif_secondaire_2", "objectifNutrition", "objectifPrincipal",
      "objectifSecondaire", "objectifSport",
    ]],
    ["Digestion et aliments problematiques", [
      "digestion", "frequenceBallonnements", "transitType", "alimentsProblematiques",
      "ballonnements_post_repas", "douleurs_digestives", "reflux",
    ]],
    ["Rythme et habitudes utiles", [
      "nbRepas", "hydratation", "mastication", "frequenceRestaurant", "niveauCuisine",
    ]],
  ];

  return groups
    .map(([label, keys]) => {
      const value = collectProfileValues(form, keys);
      return value ? `${label} : ${value}` : "";
    })
    .filter(Boolean);
}

export function buildRecipeUserMessage({ meals, form, locale = "fr", directives = "" }) {
  const lines = [];

  lines.push("=== CONTEXTE ALIMENTAIRE MINIMAL (sans identite) ===");
  const profileLines = buildRecipeProfileContextLines(form);
  lines.push(...(profileLines.length > 0 ? profileLines : ["Aucune contrainte alimentaire renseignee."]));
  lines.push("");

  const directive = redactProfileValue(directives, form);
  if (directive) {
    lines.push("=== DIRECTIVES D'ANISSA — À RESPECTER ABSOLUMENT ===");
    lines.push("Peut inclure des aliments refuses / non aimes : ne les mets dans");
    lines.push("AUCUN ingredient ni AUCUNE etape.");
    lines.push(directive);
    lines.push("");
  }

  lines.push("=== LOCALE ===");
  lines.push(locale === "en" ? "english" : "francais");
  lines.push("");

  lines.push("=== REPAS A ENRICHIR ===");
  for (const m of meals) {
    lines.push(`- key: ${m.key}`);
    lines.push(`  slot: ${m.slot} (${m.slot_label})`);
    lines.push(`  titre: ${m.title}`);
    if (m.hint) lines.push(`  hint: ${m.hint}`);
    lines.push("");
  }

  lines.push("=== TACHE ===");
  lines.push("Genere les recettes en JSON strict selon le format specifie.");
  lines.push(`Le JSON doit contenir EXACTEMENT ${meals.length} cles dans "recipes",`);
  lines.push("une par repas, avec EXACTEMENT le 'key' fourni ci-dessus.");

  return lines.join("\n");
}

const FORMAT_REPAIR_INSTRUCTION = `
=== CORRECTION DE FORMAT OBLIGATOIRE ===
La reponse precedente de ce lot ne respectait pas exactement le contrat JSON.
Renvoie tout le lot, sans commentaire, en respectant strictement chaque type.
Genere de nouveau tout le lot avec exactement les memes repas et contraintes.
Ne change pas les cles demandees. La proposition reste a valider par Anissa.
Pour chaque ingredient : label non vide, quantity nombre positif (ou null
uniquement avec unit="to_taste"), unit parmi la liste autorisee,
preparation chaine ("" si aucune), category parmi la liste autorisee.
Conserve EXACTEMENT les cles de repas demandees.
`;

// V95.4 : taille max d'un batch d'appel IA. Une recette detaillee ≈ 350-500
// tokens output. Avec max_tokens=4000, on tient ~7-8 recettes max sans risquer
// la troncature JSON. Marge de securite : 5 par lot.
const BATCH_SIZE = 5;

export class RecipeResponseValidationError extends Error {
  constructor(message, diagnostics) {
    super(message);
    this.name = "RecipeResponseValidationError";
    this.diagnostics = diagnostics ? [Object.freeze({ ...diagnostics })] : [];
  }
}

function responseValidationError(code, recipeIndex, field, ingredientIssue = null) {
  const diagnostics = {
    code,
    index: ingredientIssue?.index ?? recipeIndex ?? null,
    field: ingredientIssue?.field || field,
  };
  if (ingredientIssue && recipeIndex !== null && recipeIndex !== undefined) {
    diagnostics.recipeIndex = recipeIndex;
  }
  return new RecipeResponseValidationError(
    "Reponse IA invalide : structure de recette incomplete. A valider Anissa.",
    diagnostics,
  );
}

async function callIaForBatch(meals, {
  form, locale, directives, reinforceFormat = false,
}) {
  const baseUserMessage = buildRecipeUserMessage({ meals, form, locale, directives });
  const userMessage = reinforceFormat
    ? `${baseUserMessage}\n\n${FORMAT_REPAIR_INSTRUCTION.trim()}`
    : baseUserMessage;
  // V97.0 : passe par services/anthropic.js
  // V97.58 : system assemble par appel (bloc securite dependant de la fiche)
  const rawText = await callClaude({
    system: buildRecipeSystemPrompt(form, locale),
    user: userMessage,
    // V97.13.47 : 8000 (au lieu de 4000) — marge anti-troncature. Un batch de
    // 5 recettes detaillees peut depasser 4000 tokens de sortie et tronquer le
    // JSON → parse KO → batch perdu. 8000 laisse une large marge.
    maxTokens: 8000,
  });
  const parsed = safeParseJson(rawText);

  const recipes = parsed?.recipes || {};
  if (!recipes || typeof recipes !== "object" || Array.isArray(recipes)) {
    throw responseValidationError("INVALID_RECIPES_OBJECT", null, "recipes");
  }
  const expectedKeys = meals.map((meal) => meal.key).sort();
  const receivedKeys = Object.keys(recipes).sort();
  if (JSON.stringify(expectedKeys) !== JSON.stringify(receivedKeys)) {
    throw responseValidationError("INVALID_RECIPE_KEYS", null, "recipes");
  }
  for (let recipeIndex = 0; recipeIndex < meals.length; recipeIndex += 1) {
    const recipe = recipes[meals[recipeIndex].key];
    const hasStrictStrings = (values) => Array.isArray(values)
      && values.length > 0
      && values.every((value) => typeof value === "string" && value.trim().length > 0);
    if (!recipe || typeof recipe !== "object" || Array.isArray(recipe)) {
      throw responseValidationError("INVALID_RECIPE", recipeIndex, "recipe");
    }
    if (!hasStrictStrings(recipe.preparation)) {
      throw responseValidationError("INVALID_RECIPE_FIELD", recipeIndex, "preparation");
    }
    if (!Number.isFinite(recipe.prep_time_min) || recipe.prep_time_min < 0) {
      throw responseValidationError("INVALID_RECIPE_FIELD", recipeIndex, "prep_time_min");
    }
    if (!Number.isFinite(recipe.cook_time_min) || recipe.cook_time_min < 0) {
      throw responseValidationError("INVALID_RECIPE_FIELD", recipeIndex, "cook_time_min");
    }
    if (!Number.isFinite(recipe.servings) || recipe.servings < 1) {
      throw responseValidationError("INVALID_RECIPE_FIELD", recipeIndex, "servings");
    }
    let structuredIngredients;
    let ingredientIssue = null;
    if (Object.prototype.hasOwnProperty.call(recipe, "ingredients_structured")) {
      const result = normalizeStructuredIngredientsWithDiagnostics(recipe.ingredients_structured);
      structuredIngredients = result.ingredients;
      ingredientIssue = result.issue;
    } else {
      structuredIngredients = readStructuredRecipeIngredients(recipe);
    }
    if (!structuredIngredients) {
      throw responseValidationError(
        "INVALID_STRUCTURED_INGREDIENTS",
        recipeIndex,
        "ingredients_structured",
        ingredientIssue,
      );
    }
    recipe.ingredients_structured = structuredIngredients;
    recipe.ingredients = structuredIngredients.map(formatStructuredIngredient);
  }

  return recipes;
}

function finalGenerationError(failures, batchCount) {
  const first = failures[0];
  const detail = first instanceof ClaudeApiError && first.payload?.parseError === true
    ? "Reponse JSON IA invalide."
    : (first?.message || String(first));
  const message = `Echec de la generation des recettes apres une relance automatique : ${failures.length}/${batchCount} lots en echec. Aucun brouillon conserve. Detail : ${detail}`;
  if (first instanceof ClaudeApiError) {
    return new ClaudeApiError(message, first.status, {
      ...(first.payload && typeof first.payload === "object" ? first.payload : {}),
      recipeGeneration: { retryAttempted: true, draftsKept: false },
    });
  }
  const error = new Error(message);
  error.name = "RecipeGenerationError";
  error.diagnostics = Array.isArray(first?.diagnostics) ? first.diagnostics : [];
  return error;
}

function isRetryableFormatFailure(error) {
  return error instanceof RecipeResponseValidationError
    || (error instanceof ClaudeApiError && error.payload?.parseError === true);
}

/**
 * Genere des recettes detaillees pour une liste de repas.
 *
 * V95.4 : decoupe automatique en batches de 5 meals envoyes en parallele.
 * 18 meals → 4 appels Promise.all → latence ~= 1 appel solo, mais pas de
 * troncature JSON (chaque batch produit ~2500 tokens, large marge sous 4000).
 *
 * @param {Array<{ key, slot, slot_label, title, hint? }>} meals
 * @param {object} ctx - { form, locale }
 * @returns {Promise<Object<string, Recipe>>} - map mealKey → recipe
 */
export async function generateRecipesForMeals(meals, { form = {}, locale = "fr", directives = "" } = {}) {
  if (!Array.isArray(meals) || meals.length === 0) {
    throw new Error("Aucun repas a enrichir.");
  }
  if (!hasRiskProfileAssessment(form) || buildRecipeProfileContextLines(form).length === 0) {
    throw new Error("Profil et allergies a completer puis confirmer dans Profil.");
  }

  // V97.0 : apiKey n'est plus necessaire ici, callClaude la lit lui-meme

  // Decoupe en batches
  const batches = [];
  for (let i = 0; i < meals.length; i += BATCH_SIZE) {
    batches.push(meals.slice(i, i + BATCH_SIZE));
  }

  // Tous les lots partent en parallele, mais la proposition reste atomique :
  // un seul echec invalide l'ensemble et aucun brouillon partiel n'est rendu.
  const settled = await Promise.allSettled(
    batches.map((batch) => callIaForBatch(batch, { form, locale, directives })),
  );

  // Merge les batches reussis dans un seul objet
  const allRecipes = {};
  const failedBatches = [];
  settled.forEach((res, i) => {
    if (res.status === 'fulfilled') {
      Object.assign(allRecipes, res.value);
    } else {
      failedBatches.push({ index: i, reason: res.reason });
      console.error(`[aiRecipeGenerator] lot ${i + 1}/${batches.length} echoue`, res.reason);
    }
  });

  // Une erreur HTTP, réseau, rate-limit ou annulation n'est pas une erreur de
  // structure. La rejouer automatiquement pourrait doubler la charge sans
  // corriger la cause ; on restitue donc l'erreur API originale et typée.
  const nonRetryableFailure = failedBatches.find(({ reason }) => !isRetryableFormatFailure(reason));
  if (nonRetryableFailure) {
    if (nonRetryableFailure.reason instanceof ClaudeApiError) {
      throw nonRetryableFailure.reason;
    }
    throw finalGenerationError([nonRetryableFailure.reason], batches.length);
  }

  // Un seul rattrapage borné, uniquement pour les lots invalides. Les lots
  // valides restent en mémoire et ne sont jamais facturés/générés une seconde
  // fois. La fiche, les directives et le bloc sécurité sont strictement les
  // mêmes ; seule une consigne de format plus explicite est ajoutée.
  if (failedBatches.length > 0) {
    const retries = await Promise.allSettled(failedBatches.map(({ index }) => (
      callIaForBatch(batches[index], {
        form,
        locale,
        directives,
        reinforceFormat: true,
      })
    )));
    const retryFailures = [];
    retries.forEach((res, retryIndex) => {
      const batchIndex = failedBatches[retryIndex].index;
      if (res.status === "fulfilled") {
        Object.assign(allRecipes, res.value);
      } else {
        retryFailures.push(res.reason);
        console.error(
          `[aiRecipeGenerator] relance du lot ${batchIndex + 1}/${batches.length} echouee`,
          res.reason,
        );
      }
    });
    // Atomicité : même si d'autres lots sont valides, aucune proposition
    // partielle ne quitte le service après le second échec.
    if (retryFailures.length > 0) {
      throw finalGenerationError(retryFailures, batches.length);
    }
  }

  // Normaliser et filtrer les recettes valides
  const out = {};
  for (const meal of meals) {
    const r = allRecipes[meal.key];
    if (!r || typeof r !== "object") continue;
    const structuredIngredients = readStructuredRecipeIngredients(r);
    if (!structuredIngredients) continue;
    out[meal.key] = {
      ingredients: structuredIngredients.map(formatStructuredIngredient),
      ingredients_structured: structuredIngredients,
      preparation: r.preparation.map((value) => value.trim()),
      prep_time_min: typeof r.prep_time_min === "number" ? Math.max(0, Math.round(r.prep_time_min)) : undefined,
      cook_time_min: typeof r.cook_time_min === "number" ? Math.max(0, Math.round(r.cook_time_min)) : undefined,
      servings: typeof r.servings === "number" ? Math.max(1, Math.round(r.servings)) : undefined,
      tip: typeof r.tip === "string" ? r.tip.trim().slice(0, 250) : undefined,
    };
  }

  return out;
}
